import ApplicationErrorView from '@client/module/error/application-error-view.tsx';
import PrimaryButton from '@packages/ui/button/primary-button.tsx';
import SecondaryButton from '@packages/ui/button/secondary-button.tsx';
import CardContent from '@packages/ui/card/card-content.tsx';
import CardFooter from '@packages/ui/card/card-footer.tsx';
import CardHeader from '@packages/ui/card/card-header.tsx';
import Card from '@packages/ui/card/card.tsx';
import FormItem from '@packages/ui/form/form-item.tsx';
import FormLayout from '@packages/ui/form/form-layout.tsx';
import TextInput from '@packages/ui/form/input/text-input.tsx';
import { useNotificationCenter } from '@packages/ui/notification/notification-center.ts';
import { useTranslate } from '@tolgee/react';
import { useEffect, useState } from 'react';
import Spinner from 'ui/src/spinner';
import { useGetTable } from '../logic/use-get-table';
import { useUpdateTable } from '../logic/use-update-table';

interface TableInput {
  name: string;
  seats: number;
}

export default function UpdateTableModal(props: { tableId: string; tableGroupId: string; onClose: () => void }) {
  const { t } = useTranslate();
  const { showAlert } = useNotificationCenter();

  const { data, loading, error } = useGetTable(props.tableId);
  const [update, { error: updateError, loading: updateLoading }] = useUpdateTable();

  const [table, setTable] = useState<TableInput>({ name: '', seats: 1 });
  const [emptyValidation, setEmptyValidation] = useState<Record<string, boolean>>({
    name: false,
  });

  useEffect(() => {
    if (!data) return;

    setTable({
      name: data.table?.name ?? '',
      seats: data.table?.seats ?? 0,
    });
  }, [data]);

  if (loading) {
    return <Spinner />;
  }

  const isPassValidation = () => {
    let flag = true;
    if (table.name == '') {
      setEmptyValidation((item) => ({
        ...item,
        name: true,
      }));
      flag = false;
    }

    return flag;
  };

  const onSubmit = () => {
    if (!isPassValidation()) return;

    update({
      variables: {
        id: props.tableId,
        data: {
          name: table.name,
          seats: table.seats,
          tableGroupId: props.tableGroupId,
        },
      },
    })
      .then(() => {
        props.onClose();
        showAlert({
          status: 'success',
          message: t('table.update.alert.success.message', 'Successfully updated table'),
          title: t('table.update.alert.success.title', 'Success'),
        });
      })
      .catch((e: Error) => {
        showAlert({
          status: 'error',
          message: e.message,
          title: t('table.update.alert.error.title', 'Error'),
        });
      });
  };

  return (
    <Card>
      <CardHeader title={t('table.update.header', 'update Table')} onCrossButton={props.onClose} />

      <ApplicationErrorView error={error ?? updateError} />

      <CardContent>
        <FormLayout>
          <FormItem title={t('table.name.title', 'Name')}>
            <TextInput
              type="text"
              label="name"
              value={table.name}
              placeholder={t('table.name.placeholder', 'Name')}
              name="name"
              error={emptyValidation.name ? t('table.update.empty-error.name', 'Name is require') : undefined}
              onChange={(e) => {
                setTable({
                  ...table,
                  name: e.target.value,
                });
              }}
            />
          </FormItem>

          <FormItem title={t('table.seats.title', 'Seats')}>
            <TextInput
              type="number"
              label="seats"
              value={table.seats}
              placeholder={t('table.seats.placeholder', 'Seats')}
              name="seats"
              onChange={(e) => {
                setTable({
                  ...table,
                  seats: Number(e.target.value),
                });
              }}
            />
          </FormItem>
        </FormLayout>
      </CardContent>

      <CardFooter>
        <PrimaryButton loading={updateLoading} onClick={onSubmit}>
          {t('common.button-update', 'Update')}
        </PrimaryButton>
        <SecondaryButton onClick={props.onClose}>{t('common.button.cancel', 'Cancel')}</SecondaryButton>
      </CardFooter>
    </Card>
  );
}
